import type { ComparableFmv, ComparableFmvConfidence } from '../dto/comparable-fmv.js';
import { formatUsd } from './format';
import { SectionLabel } from './SectionLabel';
import { cn } from './utils';

/**
 * SIL-1155: comparable-FMV panel, shared by the customer and ops portals.
 *
 * Deliberately NOT styled like the asset's own FMV: dashed hairline on a muted
 * ground, small type, an explicit "informational, not this asset's FMV" title.
 * The comparable estimate is borrowed from equivalence-group siblings' comps and
 * must never read as authoritative. It always renders as a range plus a
 * confidence label, never a bare number, and renders nothing when the API omitted
 * the field (flag off, not a gaming_pc, no estimate, or suppressed by the gate).
 */
const CONFIDENCE_LABEL: Record<ComparableFmvConfidence, string> = {
  low: 'Low confidence',
  medium: 'Medium confidence',
  high: 'High confidence',
};

export function ComparableFmvPanel({
  comparable,
  className,
}: {
  comparable: ComparableFmv | null | undefined;
  className?: string;
}) {
  if (!comparable) return null;
  const range = `${formatUsd(comparable.low)}-${formatUsd(comparable.high)}`;
  const label = CONFIDENCE_LABEL[comparable.confidence];
  return (
    <section
      aria-label="Comparable estimate"
      data-testid="comparable-fmv-panel"
      className={cn('rounded-lg border border-dashed border-border bg-muted/40 px-4 py-3', className)}
    >
      <SectionLabel>Comparable estimate - informational, not this asset&apos;s FMV</SectionLabel>
      <p className="mt-1.5 text-sm font-medium text-foreground">
        <span data-testid="comparable-fmv-range">{range}</span>{' '}
        <span data-testid="comparable-fmv-confidence" className="font-normal text-muted-foreground">
          ({label.toLowerCase()})
        </span>
      </p>
      <p className="mt-1 text-xs text-muted-foreground">
        Based on recent market prices of comparable GPU models, not this exact configuration. Use it as
        context only.
      </p>
    </section>
  );
}
